import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Expand,
  Heart,
  Pause,
  Play,
  RefreshCw,
  Wifi,
  WifiOff,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { TvData } from "../types";
import { buildTvPlaylist, type TvSlide } from "../tvPlaylist";

const cacheKey = "makyatom-tv-playlist";

export function TvPage() {
  const [data, setData] = useState<TvData | null>(() => {
    try {
      const cached = localStorage.getItem(cacheKey);
      return cached ? (JSON.parse(cached) as TvData) : null;
    } catch {
      return null;
    }
  });
  const [online, setOnline] = useState(navigator.onLine);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progressKey, setProgressKey] = useState(0);
  const wakeLock = useRef<WakeLockSentinel | null>(null);

  const synchronize = useCallback(async () => {
    try {
      const result = await api<TvData>("/api/tv");
      setData(result);
      localStorage.setItem(cacheKey, JSON.stringify(result));
      setOnline(true);
    } catch {
      setOnline(false);
    }
  }, []);

  useEffect(() => {
    void synchronize();
    const poll = window.setInterval(() => void synchronize(), 30_000);
    const handleOnline = () => void synchronize();
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [synchronize]);

  const slides = useMemo<TvSlide[]>(
    () => (data ? buildTvPlaylist(data) : []),
    [data],
  );

  useEffect(() => {
    if (!data) return;
    const images = (data.photos ?? []).map((photo) => {
      const image = new Image();
      image.src = photo.imageUrl;
      return image;
    });
    return () => images.forEach((image) => image.removeAttribute("src"));
  }, [data]);

  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(slides.length - 1, 0)));
  }, [slides.length]);

  const goTo = useCallback(
    (next: number) => {
      if (!slides.length) return;
      setIndex((next + slides.length) % slides.length);
      setProgressKey((value) => value + 1);
    },
    [slides.length],
  );

  useEffect(() => {
    if (!playing || !data || slides.length < 2) return;
    const timer = window.setTimeout(
      () => goTo(index + 1),
      data.tv.intervalSeconds * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [data, goTo, index, playing, progressKey, slides.length]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") goTo(index + 1);
      if (event.key === "ArrowLeft") goTo(index - 1);
      if (event.key === " ") {
        event.preventDefault();
        setPlaying((value) => !value);
      }
      if (event.key.toLowerCase() === "f") {
        void document.documentElement.requestFullscreen?.();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goTo, index]);

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        wakeLock.current = await navigator.wakeLock?.request("screen");
      } catch {
        wakeLock.current = null;
      }
    };
    void requestWakeLock();
    return () => void wakeLock.current?.release();
  }, []);

  if (!data || !slides.length) {
    return (
      <main className="tv-empty">
        <Heart fill="currentColor" />
        <h1>TV režim čeká na první synchronizaci</h1>
        <button className="button button--primary" onClick={() => void synchronize()}>
          <RefreshCw /> Načíst znovu
        </button>
      </main>
    );
  }

  const slide = slides[Math.min(index, slides.length - 1)];

  return (
    <main className="tv" data-slide={slide.type}>
      <header className="tv__header">
        <strong>Markéta <span>&</span> Tomáš</strong>
        <div className="tv__status">
          {online ? <Wifi size={18} /> : <WifiOff size={18} />}
          <span>{online ? "online" : "offline · uložený obsah"}</span>
        </div>
      </header>

      <div
        key={`${slide.id}-${progressKey}`}
        className={`tv__progress ${playing ? "is-playing" : ""}`}
        style={{ "--duration": `${data.tv.intervalSeconds}s` } as React.CSSProperties}
      >
        <span />
      </div>

      <section className="tv__stage">
        {slide.type === "welcome" && (
          <div className="tv-welcome">
            <Heart fill="currentColor" />
            <span>Dnes slavíme</span>
            <h1>{data.site.firstName} <i>&</i> {data.site.secondName}</h1>
            <p>{data.tv.announcement}</p>
          </div>
        )}
        {slide.type === "memory" && (
          <article className="tv-memory">
            <Heart fill="currentColor" />
            <span>Vzpomínka od hostů</span>
            <h1>{slide.memory.title}</h1>
            <p>{slide.memory.body}</p>
            <footer>— {slide.memory.author}</footer>
          </article>
        )}
        {slide.type === "photo" && (
          <figure className="tv-photo">
            <img
              src={slide.photo.imageUrl}
              alt={slide.photo.caption || "Svatební fotografie"}
              width={slide.photo.width}
              height={slide.photo.height}
            />
            {slide.photo.caption && (
              <figcaption>{slide.photo.caption}</figcaption>
            )}
          </figure>
        )}
        {slide.type === "program" && (
          <div className="tv-program">
            <span>Co nás dnes čeká</span>
            <h1>Program svatebního dne</h1>
            <ol>
              {data.program.map((item) => (
                <li key={`${item.time}-${item.title}`}>
                  <time>{item.time}</time>
                  <i />
                  <div><strong>{item.title}</strong><small>{item.detail}</small></div>
                </li>
              ))}
            </ol>
          </div>
        )}
        {slide.type === "gallery" && (
          <div className="tv-gallery">
            <div>
              <Camera />
              <span>MakyTom momentky</span>
              <h1>Přidejte pohled na svatbu vašima očima.</h1>
              <p>
                Naskenujte QR kód a pokračujte na samostatné stránce
                MyWediary.
              </p>
            </div>
            <aside>
              <QRCodeSVG
                value={data.site.wediaryUrl}
                size={280}
                bgColor="#ffffff"
                fgColor="#17392b"
                level="M"
              />
              <strong>MYWEDIARY · naskenujte fotoaparátem</strong>
            </aside>
          </div>
        )}
      </section>

      <footer className="tv__footer">
        <span>{index + 1} / {slides.length}</span>
        <div className="tv__controls">
          <button onClick={() => goTo(index - 1)} title="Předchozí" aria-label="Předchozí"><ArrowLeft /></button>
          <button onClick={() => setPlaying((value) => !value)} title={playing ? "Pozastavit" : "Přehrát"} aria-label={playing ? "Pozastavit" : "Přehrát"}>
            {playing ? <Pause /> : <Play />}
          </button>
          <button onClick={() => goTo(index + 1)} title="Další" aria-label="Další"><ArrowRight /></button>
          <button onClick={() => void document.documentElement.requestFullscreen?.()} title="Celá obrazovka" aria-label="Celá obrazovka"><Expand /></button>
        </div>
        <span>makyatom.fun</span>
      </footer>
    </main>
  );
}
